import { shuffledBag } from './randomizer';
import { getGameMode, levelForLines } from './modes';
import { getKickOffsets, getPieceCells } from './tetrominoes';
import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  PREVIEW_COUNT,
  type ActivePiece,
  type Board,
  type GameEvent,
  type GameModeId,
  type GameState,
  type LineTarget,
  type PauseReason,
  type Rotation,
  type TetrominoType,
} from './types';

const LINE_SCORES = [0, 100, 300, 500, 800] as const;

export function createEmptyBoard(): Board {
  return Array.from({ length: BOARD_HEIGHT }, () => Array.from({ length: BOARD_WIDTH }, () => null));
}

export function spawnPiece(type: TetrominoType): ActivePiece {
  return { type, rotation: 0, x: 3, y: 0 };
}

function refillQueue(state: GameState, minimum = PREVIEW_COUNT): GameState {
  const queue = [...state.queue];
  let bag = [...state.bag];
  let rngSeed = state.rngSeed;

  while (queue.length < minimum) {
    if (bag.length === 0) {
      const refill = shuffledBag(rngSeed);
      bag = refill.bag;
      rngSeed = refill.seed;
    }
    queue.push(bag.shift()!);
  }

  return { ...state, queue, bag, rngSeed };
}

function takeNextPiece(state: GameState, canHold = getGameMode(state.mode).holdEnabled): GameState {
  const ready = refillQueue(state, PREVIEW_COUNT + 1);
  const active = spawnPiece(ready.queue[0]);
  return refillQueue({
    ...ready,
    active,
    queue: ready.queue.slice(1),
    canHold,
  });
}

export function createGame(
  seed = Date.now(),
  status: GameState['status'] = 'playing',
  mode: GameModeId = 'regular',
): GameState {
  const normalizedSeed = (seed >>> 0) || 0x6d2b79f5;
  const base: GameState = {
    mode,
    board: createEmptyBoard(),
    active: null,
    queue: [],
    bag: [],
    held: null,
    canHold: getGameMode(mode).holdEnabled,
    score: 0,
    lines: 0,
    quads: 0,
    level: levelForLines(mode, 0),
    lineTarget: 1,
    status,
    pauseReason: null,
    rngSeed: normalizedSeed,
    eventId: 0,
    lastEvent: null,
  };
  const game = takeNextPiece(base);
  return status === 'idle' ? { ...game, active: null } : game;
}

export function collides(board: Board, piece: ActivePiece): boolean {
  return getPieceCells(piece).some(({ x, y }) => (
    x < 0 || x >= BOARD_WIDTH || y >= BOARD_HEIGHT || (y >= 0 && board[y][x] !== null)
  ));
}

function translate(state: GameState, deltaX: number, deltaY: number): GameState {
  if (state.status !== 'playing' || !state.active) return state;
  const active = { ...state.active, x: state.active.x + deltaX, y: state.active.y + deltaY };
  return collides(state.board, active) ? state : { ...state, active };
}

export function movePiece(state: GameState, direction: -1 | 1): GameState {
  return translate(state, direction, 0);
}

export function stepDown(state: GameState): GameState {
  return translate(state, 0, 1);
}

export function softDrop(state: GameState): GameState {
  const moved = stepDown(state);
  return moved === state ? state : { ...moved, score: moved.score + 1 };
}

export function rotatePiece(state: GameState, direction: -1 | 1): GameState {
  if (state.status !== 'playing' || !state.active) return state;
  const from = state.active.rotation;
  const to = ((from + direction + 4) % 4) as Rotation;

  for (const offset of getKickOffsets(state.active.type, from, to)) {
    const candidate: ActivePiece = {
      ...state.active,
      rotation: to,
      x: state.active.x + offset.x,
      y: state.active.y + offset.y,
    };
    if (!collides(state.board, candidate)) return { ...state, active: candidate };
  }

  return state;
}

export function isGrounded(state: GameState): boolean {
  if (!state.active) return false;
  return collides(state.board, { ...state.active, y: state.active.y + 1 });
}

export function getGhostPiece(state: GameState): ActivePiece | null {
  if (!state.active) return null;
  let ghost = { ...state.active };
  while (!collides(state.board, { ...ghost, y: ghost.y + 1 })) {
    ghost = { ...ghost, y: ghost.y + 1 };
  }
  return ghost;
}

export function clearCompletedLines(board: Board): { board: Board; rows: number[] } {
  const rows: number[] = [];
  const remaining = board.filter((row, index) => {
    const complete = row.every((cell) => cell !== null);
    if (complete) rows.push(index);
    return !complete;
  });
  const emptyRows = Array.from({ length: rows.length }, () => Array.from({ length: BOARD_WIDTH }, () => null));
  return { board: [...emptyRows, ...remaining], rows };
}

export function scoreForLines(count: number, level: number): number {
  return (LINE_SCORES[Math.min(count, 4)] ?? 0) * level;
}

function eventFor(
  state: GameState,
  cause: 'gravity' | 'hardDrop',
  clearedRows: number[],
  dropDistance: number,
  levelUp: boolean,
  gameOver: boolean,
  targetMet = false,
): GameEvent {
  return {
    id: state.eventId + 1,
    kind: gameOver ? 'gameOver' : clearedRows.length > 0 ? 'lineClear' : cause,
    clearedRows,
    dropDistance,
    levelUp,
    targetMet,
  };
}

export function lockActive(
  state: GameState,
  cause: 'gravity' | 'hardDrop' = 'gravity',
  dropDistance = 0,
): GameState {
  if (state.status !== 'playing' || !state.active) return state;
  const cells = getPieceCells(state.active);

  if (cells.some(({ y }) => y < 0)) {
    const lastEvent = eventFor(state, cause, [], dropDistance, false, true);
    return { ...state, active: null, status: 'gameOver', eventId: lastEvent.id, lastEvent };
  }

  const board = state.board.map((row) => [...row]);
  for (const { x, y } of cells) board[y][x] = state.active.type;

  const cleared = clearCompletedLines(board);
  const lines = state.lines + cleared.rows.length;
  const level = levelForLines(state.mode, lines);
  const levelUp = level > state.level;
  const mode = getGameMode(state.mode);
  const targetMet = mode.scoringRule === 'contract' && cleared.rows.length === state.lineTarget;
  const lineScore = mode.scoringRule === 'contract'
    ? targetMet ? scoreForLines(cleared.rows.length, state.level) * 3 : 0
    : scoreForLines(cleared.rows.length, state.level);
  const lineTarget = targetMet ? ((state.lineTarget % 4) + 1) as LineTarget : state.lineTarget;
  let next = takeNextPiece({
    ...state,
    board: cleared.board,
    active: null,
    score: state.score + lineScore,
    lines,
    quads: state.quads + (cleared.rows.length === 4 ? 1 : 0),
    level,
    lineTarget,
    canHold: true,
  });

  const gameOver = next.active ? collides(next.board, next.active) : true;
  const lastEvent = eventFor(state, cause, cleared.rows, dropDistance, levelUp, gameOver, targetMet);
  next = {
    ...next,
    active: gameOver ? null : next.active,
    status: gameOver ? 'gameOver' : 'playing',
    eventId: lastEvent.id,
    lastEvent,
  };
  return next;
}

export function hardDrop(state: GameState): GameState {
  if (state.status !== 'playing' || !state.active) return state;
  let active = { ...state.active };
  let distance = 0;
  while (!collides(state.board, { ...active, y: active.y + 1 })) {
    active = { ...active, y: active.y + 1 };
    distance += 1;
  }
  return lockActive({ ...state, active, score: state.score + distance * 2 }, 'hardDrop', distance);
}

export function holdPiece(state: GameState): GameState {
  if (!getGameMode(state.mode).holdEnabled || state.status !== 'playing' || !state.active || !state.canHold) return state;
  const outgoing = state.active.type;
  let next: GameState;

  if (state.held) {
    next = { ...state, active: spawnPiece(state.held), held: outgoing, canHold: false };
  } else {
    next = takeNextPiece({ ...state, active: null, held: outgoing }, false);
  }

  const gameOver = next.active ? collides(next.board, next.active) : true;
  const lastEvent: GameEvent = {
    id: state.eventId + 1,
    kind: gameOver ? 'gameOver' : 'hold',
    clearedRows: [],
    dropDistance: 0,
    levelUp: false,
    targetMet: false,
  };
  return {
    ...next,
    active: gameOver ? null : next.active,
    status: gameOver ? 'gameOver' : next.status,
    eventId: lastEvent.id,
    lastEvent,
  };
}

export function pauseGame(state: GameState, reason: Exclude<PauseReason, null> = 'manual'): GameState {
  return state.status === 'playing' ? { ...state, status: 'paused', pauseReason: reason } : state;
}

export function resumeGame(state: GameState): GameState {
  return state.status === 'paused' ? { ...state, status: 'playing', pauseReason: null } : state;
}

export function togglePause(state: GameState): GameState {
  return state.status === 'paused' ? resumeGame(state) : pauseGame(state);
}

export function gravityInterval(level: number, mode: GameModeId = 'regular'): number {
  const modeDefinition = getGameMode(mode);
  if (modeDefinition.gravityEnabled === false) return Number.POSITIVE_INFINITY;
  const fixedGravityMs = modeDefinition.fixedGravityMs;
  if (fixedGravityMs !== null) return fixedGravityMs;
  return Math.max(75, Math.round(900 * Math.pow(0.82, level - 1)));
}
